import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";

import { clearAuth, fetchWithAuth, isAuthenticated } from "../../auth/auth";
import { getApiUrl } from "../../utils/getApiUrl";
import { handleGoogleLogin } from "../../pages/login/googleAuth";
import { processLoginResponse } from "../../pages/login/loginAction";

import { BaseModal } from "../../components/modalCommon/modalBase";
import FormActionButton from "../../components/formActionButton/formActionButton";
import { ModalHeader } from "../../components/modalCommon/modalHeader";
import { ModalBody } from "../../components/modalCommon/modalBody";
import { useSpinner } from "../../components/spinner/spinner";
import { useI18n } from "../../utils/translate/i18n/useI18n";
import type { LoginModalModel } from "../../type/login";


const LoginModal: React.FC<LoginModalModel> = ({ isOpen, onClose, onOpenRegister }) => {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { showSpinner, hideSpinner } = useSpinner();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    clearAuth();

    if (!email || !password) {
      setError(t("Wprowadź adres e-mail i hasło."));
      return;
    }

    setLoading(true);
    try {
      const resp = await fetchWithAuth(getApiUrl("auth/login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: email, password }),
      });

      if (!resp.ok) {
        setError(t("Logowanie nie powiodło się: nieprawidłowy login lub hasło."));
        return;
      }

      const data = await resp.json();
      const success = processLoginResponse({ data, navigate, onError: setError });
      if (success) onClose();
    } catch (err: unknown) {
      console.error(err);
      setError(err instanceof Error ? err.message : t("Błąd sieci."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal open={isOpen} onClose={onClose} size="sm">
      <ModalHeader title={t("Zaloguj się")} onClose={onClose} />

      <form onSubmit={handleSubmit}>
        <ModalBody>
          {/* Email */}
          <div className="mb-3">
            <label className="form-label">{t("Adres e-mail")}</label>
            <input
              type="email"
              className="form-control text-light border-secondary"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>

          {/* Password */}
          <div className="mb-3">
            <label className="form-label">{t("Hasło")}</label>
            <input
              type="password"
              className="form-control text-light border-secondary"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <div className="text-end mt-2">
              <FormActionButton
                type="button"
                variant="ghost"
                size="sm"
                iconClassName="bi bi-key-fill"
                onClick={() => {
                  const emailQuery = email.trim() ? `?email=${encodeURIComponent(email.trim())}` : "";
                  onClose();
                  navigate(`/reset-password${emailQuery}`);
                }}
              >
                {t("Nie pamiętasz hasła?")}
              </FormActionButton>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="alert alert-danger py-2 mb-3">{error}</div>
          )}

          {/* Submit */}
          <FormActionButton
            type="submit"
            fullWidth
            iconClassName="bi bi-box-arrow-in-right"
            disabled={loading}
            loading={loading}
            loadingLabel={t("Logowanie…")}
          >
            {t("Zaloguj się")}
          </FormActionButton>
        </ModalBody>
      </form>

      <div className="text-center my-3">{t("lub")}</div>

      <div className="d-flex justify-content-center">
        <GoogleLogin
          onSuccess={(res) =>
            handleGoogleLogin(res.credential, navigate, setError, showSpinner, hideSpinner, onClose)
          }
          onError={() => setError(t("Logowanie przez Google nie powiodło się."))}
        />
      </div>

      {/* <div className="text-center mt-3 px-4">
        <small className="text-secondary d-inline-block lh-sm">
          <i className="bi bi-shield-lock me-1" aria-hidden="true" />
          {t("Logując się, potwierdzasz zapoznanie się z Polityką prywatności i zasadami przetwarzania danych w serwisie.")}
        </small>
      </div> */}

      {onOpenRegister && !isAuthenticated() && (
        <div className="text-center mt-3">
          <small>
            {t("Nie masz konta?")}{" "}
            <FormActionButton
              type="button"
              variant="ghost"
              size="sm"
              iconClassName="bi bi-person-plus"
              onClick={() => { onOpenRegister(); }}
            >
              {t("Zarejestruj się")}
            </FormActionButton>
          </small>
        </div>
      )}

      <div className="text-center mb-3 mt-3">
        <small>
          <Link
            to="/privacy-policy"
            onClick={onClose}
            className="text-decoration-none d-inline-flex align-items-center gap-1"
          >
            <i className="bi bi-file-earmark-text" aria-hidden="true" />
            {t("Polityka prywatności")}
          </Link>
        </small>
      </div>
    </BaseModal>
  );
};

export default LoginModal;
